import cn from 'classnames';
import { useEffect, useState, useContext, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import AutoBlurButton from 'components/common/AutoBlurButton';
import Text from 'components/common/Text';
import IdMeCheckMark from 'components/icons/IdMeCheckMark';
import CaretUp from 'components/icons/navigation/CaretUp';
import IdMeVerify from 'components/shared/IdMeVerify';
import { BrandContext, TEMPO } from 'context/BrandContext';
import useBrand from 'hooks/useBrand';
import styles from 'styles/components/signUpFunnel/promo/IdMeToggle';

const IdMeToggle = ({
  hasAdditionalPromo = false,
  hasIdMe = false,
  endpoint,
  headerText,
  offerText,
  recurringDiscountText,
  isBilling = false,
}) => {
  const [showIdMeBox, setShowIdMeBox] = useState(false);
  const { brand } = useContext(BrandContext);
  const contentId = isBilling ? 'idme-billing-content' : 'idme-promo-content';
  const transitionRef = useRef();
  const isTempo = useBrand(TEMPO);

  useEffect(() => {
    if (hasIdMe) {
      setShowIdMeBox(true);
    }
  }, [hasIdMe]);

  const ShowIdMeVerify = () => (
    <div className="px-4">
      <Text brandStylesOverrides="textSm">{offerText}</Text>
      <IdMeVerify endpoint={endpoint} brand={brand} />
    </div>
  );

  const ShowPromoText = () => (
    <>
      <IdMeCheckMark />
      <Text brandStylesOverrides="textSm text-primary" className="-mt-6 ml-10 mr-20 pb-4 bp2:mr-28">
        {hasAdditionalPromo ? recurringDiscountText : 'Your ID.me discount has been applied.'}
      </Text>
    </>
  );

  return (
    <div
      className={cn('mt-2 rounded-2xl border border-solid bg-white-100 text-left', {
        'w-full': !isBilling,
        'm-auto': isBilling,
        [styles.checkoutIdMeBox]: isBilling,
        'border-cream-120': !isTempo,
        'border-tempo-gray-100': isTempo,
      })}
    >
      <AutoBlurButton
        type="button"
        onClick={() => setShowIdMeBox(!showIdMeBox)}
        className="btn flex w-full items-center py-0 pl-0 pr-4 text-left"
        aria-expanded={showIdMeBox}
        aria-controls={contentId}
        id={`${contentId}-button`}
      >
        <Text
          htmlElement="span"
          brandStylesOverrides="textSm"
          className="mb-0 flex h-14 w-full items-center pl-4 pr-0 font-medium text-charcoal-100"
        >
          {headerText}
        </Text>
        <CaretUp
          className={cn('text-charcoal-100 transition-all bp2:h-6 bp2:w-6', {
            ['rotate-180 transform']: !showIdMeBox,
          })}
        />
      </AutoBlurButton>
      <CSSTransition
        nodeRef={transitionRef}
        in={showIdMeBox}
        timeout={500}
        classNames={{
          enter: 'fadeInOut-enter scaleHeight-enter',
          enterActive: 'fadeInOut-enter-active scaleHeight-enter-active',
          exit: 'fadeInOut-exit scaleHeight-exit',
          exitActive: 'fadeInOut-exit-active scaleHeight-exit-active',
        }}
        unmountOnExit
      >
        <div
          ref={transitionRef}
          id={contentId}
          role="region"
          aria-labelledby={`${contentId}-button`}
        >
          {hasIdMe ? <ShowPromoText /> : <ShowIdMeVerify />}
        </div>
      </CSSTransition>
    </div>
  );
};

IdMeToggle.propTypes = {
  endpoint: PropTypes.string.isRequired,
  hasAdditionalPromo: PropTypes.bool,
  hasIdMe: PropTypes.bool,
  headerText: PropTypes.string.isRequired,
  recurringDiscountText: PropTypes.string,
  offerText: PropTypes.string.isRequired,
  isBilling: PropTypes.bool,
};

export default IdMeToggle;
